import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  IconButton,
  MenuDivider,
  MenuItem,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { DefaultTableProps } from "./interface";
import { PaginatorInfo } from "../../generated/graphql";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ArrowPagination from "../ArrowPagination";
import { Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import OptionMenu from "../OptionMenu";

function DefaultTable<TData, TFilter>({
  onPageChange,
  columns,
  data,
  getRowId,
  filterComponent,
  beforeTableComponent,
  subComponent,
  paginator,
  tableContainerProps,
  hasRowSelection,
  hasRowSubComponent,
  hasMenu,
  menuItems,
  onSelect,
  onEdit,
  onDelete,
  isLoading,
  columnVisibility,
  hideTopDivider,
}: DefaultTableProps<TData, TFilter>) {
  const [selectedRow, setSelectedRow] = useState<{
    rowId: string;
    data: TData;
  } | null>(null);

  const table = useReactTable({
    data: data as TData[],
    columns: columns as ColumnDef<TData, any>[],
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowCanExpand: () => true,
    initialState: {
      columnVisibility,
    },
  });

  useEffect(() => {
    setSelectedRow(null);
  }, [data]);

  return (
    <>
      {filterComponent}
      {!hideTopDivider && <Divider />}
      {beforeTableComponent}
      <Box {...tableContainerProps}>
        {isLoading ? (
          <Flex alignItems="center" justifyContent="center" minH="150px">
            <Spinner />
          </Flex>
        ) : (
          <Table>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {hasRowSelection && <Td w="1px"></Td>}
                  {hasRowSubComponent && <Td w="1px"></Td>}
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <Tr
                    onClick={() => {
                      setSelectedRow({
                        rowId: row.id,
                        data: row.original,
                      });
                    }}
                  >
                    {hasRowSelection && (
                      <Td
                        sx={{
                          label: {
                            verticalAlign: "middle",
                          },
                        }}
                      >
                        <Checkbox isChecked={selectedRow?.rowId === row.id} />
                      </Td>
                    )}
                    {hasRowSubComponent && (
                      <Td>
                        {row.getCanExpand() && (
                          <IconButton
                            variant="icon"
                            aria-label="Expand"
                            {...{
                              onClick: row.getToggleExpandedHandler(),
                            }}
                            icon={
                              row.getIsExpanded() ? (
                                <ChevronDownIcon boxSize="22px" />
                              ) : (
                                <ChevronRightIcon boxSize="22px" />
                              )
                            }
                          />
                        )}
                      </Td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                    {hasMenu && (
                      <Td textAlign="right">
                        <OptionMenu
                          items={
                            (menuItems && menuItems(row.original)) || [
                              <MenuItem
                                onClick={() => onEdit && onEdit(row.original)}
                              >
                                Edit
                              </MenuItem>,
                              <MenuDivider />,
                              <MenuItem
                                onClick={() =>
                                  onDelete && onDelete(row.original)
                                }
                              >
                                Delete
                              </MenuItem>,
                            ]
                          }
                        />
                      </Td>
                    )}
                  </Tr>
                  {hasRowSubComponent && row.getIsExpanded() && (
                    <Tr bg="rgb(250, 250, 250)">
                      {hasRowSelection ? <Td colSpan={2}></Td> : <Td></Td>}
                      <Td
                        p={0}
                        colSpan={
                          row.getVisibleCells().length + (hasMenu ? 1 : 0)
                        }
                      >
                        {subComponent ? subComponent({ row }) : <></>}
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      {paginator && (
        <Flex
          mt={6}
          justifyContent={hasRowSelection ? "space-between" : "flex-end"}
        >
          <ArrowPagination
            paginator={paginator as PaginatorInfo}
            onPrev={(value: number) => {
              onPageChange && onPageChange(value);
            }}
            onNext={(value: number) => {
              onPageChange && onPageChange(value);
            }}
          />
          {hasRowSelection && (
            <Button
              isDisabled={!selectedRow}
              onClick={() => {
                onSelect && onSelect(selectedRow?.data as TData);
              }}
            >
              Select
            </Button>
          )}
        </Flex>
      )}
    </>
  );
}

export default DefaultTable;
